import { useState, useEffect } from 'react';
import { API_CONFIG } from '../config/api';

interface MigrationStats {
  total: number;
  en_drive: number;
  pendientes: number;
  sin_ruta: number;
  porcentaje_migrado: string;
}

interface MigrationStatsResponse {
  success: boolean;
  rut: string;
  data: MigrationStats;
}

interface CheckMigrationResponse {
  success: boolean;
  message: string;
  rut: string;
}

/**
 * Hook para migración automática de documentos a Google Drive
 * 
 * Características:
 * - Verifica documentos pendientes de migración
 * - Inicia migración en segundo plano (fire and forget)
 * - No bloquea la navegación del usuario
 * - Procesa máximo 20 documentos por sesión
 * 
 * @param rut - RUT del usuario para verificar documentos
 * @param autoCheck - Si debe verificar automáticamente al montar (default: true)
 */
export const useAutoMigration = (rut?: string, autoCheck: boolean = true) => {
  const [migrando, setMigrando] = useState(false);
  const [stats, setStats] = useState<MigrationStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  /**
   * Obtiene estadísticas de migración para el usuario
   */
  const getStats = async (rutParam: string): Promise<MigrationStats | null> => {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/migracion/stats-personal/${rutParam}`
      );
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data: MigrationStatsResponse = await response.json();
      
      if (data.success && data.data) {
        setStats(data.data);
        return data.data;
      }
      
      return null;
    } catch (err: any) {
      console.error('Error obteniendo estadísticas de migración:', err);
      setError(err.message || 'Error al obtener estadísticas');
      return null;
    }
  };

  /**
   * Inicia la verificación y migración de documentos
   * Fire and forget - no espera respuesta
   */
  const checkDocuments = async (rutParam: string): Promise<void> => {
    try {
      // Fire and forget - no bloqueamos con await
      fetch(`${API_CONFIG.BASE_URL}/migracion/check-personal/${rutParam}`)
        .then(async (response) => {
          if (!response.ok) {
            console.warn(`Verificación respondió con status ${response.status}`);
            return;
          }
          
          const data: CheckMigrationResponse = await response.json();
          
          if (data.success) {
            console.log('✅', data.message);
          }
        })
        .catch((err) => {
          console.warn('⚠️ Error en verificación background:', err);
        });
    } catch (err) {
      console.warn('⚠️ Error iniciando verificación:', err);
    }
  };

  /**
   * Verifica si hay documentos pendientes y los migra automáticamente
   */
  const verificarYMigrar = async (rutParam?: string): Promise<void> => {
    const rutToUse = rutParam || rut;
    
    if (!rutToUse) {
      console.warn('RUT no proporcionado para migración');
      return;
    }

    try {
      setError(null);
      setMigrando(true);
      setLastCheck(new Date());

      // 1. Obtener estadísticas
      const currentStats = await getStats(rutToUse);

      if (!currentStats) {
        setMigrando(false);
        return;
      }

      // 2. Si hay documentos pendientes, iniciar migración en background
      if (currentStats.pendientes > 0) {
        console.log(`📋 ${currentStats.pendientes} documento(s) pendiente(s) de migración para ${rutToUse}`);
        
        // Fire and forget - la migración se ejecuta en background
        await checkDocuments(rutToUse);
        
        // Mostrar feedback visual durante 3 segundos
        setTimeout(() => {
          setMigrando(false);
        }, 3000);
      } else {
        console.log('✅ Todos los documentos ya están en Drive');
        setMigrando(false);
      }
    } catch (err: any) {
      console.error('Error en verificación y migración:', err);
      setError(err.message || 'Error en migración');
      setMigrando(false);
    }
  };

  /**
   * Refresca las estadísticas sin iniciar nueva migración
   */
  const refreshStats = async (rutParam?: string): Promise<void> => {
    const rutToUse = rutParam || rut;
    
    if (!rutToUse) return;

    await getStats(rutToUse);
  };

  // Auto-verificación al montar el componente
  useEffect(() => {
    if (autoCheck && rut) {
      verificarYMigrar(rut);
    }
  }, [rut, autoCheck]);

  return {
    migrando,
    stats,
    error,
    lastCheck,
    verificarYMigrar,
    refreshStats,
    getStats,
  };
};

export default useAutoMigration;
